import type { BooleanKind } from '@anytracker/core'

import styles from './BooleanInput.module.css'
import type { ValueInputProps } from './types.js'
import { useRadioGroupKeys } from './useRadioGroupKeys.js'

interface Props extends ValueInputProps {
  readonly kind: BooleanKind
}

/**
 * A two-state choice showing the track's own labels.
 *
 * Never "true"/"false" — the track author wrote "Did it"/"Skipped" or
 * "Yes"/"No" for a reason, and a control that ignores them makes every boolean
 * track read identically.
 *
 * Built as a radio group rather than a switch. A switch implies a default
 * on/off state; this has three, the third being *unanswered*, and re-pressing
 * the selected option returns to it.
 */
export function BooleanInput({
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
}: Props) {
  const current = typeof value === 'boolean' ? value : null

  const options = [
    { state: true, label: kind.trueLabel },
    { state: false, label: kind.falseLabel },
  ]

  const selectedIndex = options.findIndex((option) => option.state === current)
  const keys = useRadioGroupKeys(options.length, selectedIndex, (index) => {
    const option = options[index]
    if (option) {
      onChange(option.state)
    }
  })

  return (
    <div
      className={styles['root']}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {options.map(({ state, label }, index) => {
        const selected = current === state
        return (
          <button
            key={String(state)}
            ref={keys.register(index)}
            type="button"
            onKeyDown={keys.onKeyDown}
            role="radio"
            aria-checked={selected}
            tabIndex={keys.tabIndexFor(index)}
            disabled={disabled}
            className={`${styles['option']} ${selected ? styles['selected'] : ''}`}
            // Pressing the selected option clears it. Without this, a mis-tap
            // is unrecoverable without abandoning the entry, and the user
            // learns to record a wrong answer rather than no answer.
            onClick={() => onChange(selected ? null : state)}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
